import React, { createContext, useContext, useState, ReactNode } from 'react';

interface BatchJob {
    _id: string;
    status: string;
    productData?: {
        id: string;
        name: string;
    };
    resultProduct?: {
        thumbnailPath?: string;
    };
    error?: string;
    errorDetails?: {
        userMessage?: string;
    };
}

interface BatchError {
    productId: string;
    productName: string;
    errorMessage: string;
    suggestion: string;
    retryable: boolean;
    field?: string;
    currentValue?: any;
    category?: string;
    jobId?: string;
}

interface BatchStatus {
    batchId: string;
    total: number;
    pending: number;
    generating_seamless: number;
    processing: number;
    completed: number;
    failed: number;
    percentage: number;
    jobs?: BatchJob[];
    errors?: BatchError[];
}

// Import page state that needs to persist across navigation
interface ImportPageState {
    editProducts: any[]; // Generic product data for import
    images: File[];
    savedProductIds: Set<string>;
    selectedProductIds: Set<string>;
    expandedProducts: { [key: string]: boolean };
    processingProductIds: Set<string>;
    productsMissingFiles: Set<string>;
}

interface BatchProcessingContextType {
    batchStatus: BatchStatus | null;
    setBatchStatus: React.Dispatch<React.SetStateAction<BatchStatus | null>>;
    isBatchProcessing: boolean;
    setIsBatchProcessing: React.Dispatch<React.SetStateAction<boolean>>;
    batchComplete: boolean;
    setBatchComplete: React.Dispatch<React.SetStateAction<boolean>>;
    isPanelMinimized: boolean;
    setIsPanelMinimized: React.Dispatch<React.SetStateAction<boolean>>;
    isPanelExpanded: boolean;
    setIsPanelExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    isPanelVisible: boolean;
    setIsPanelVisible: React.Dispatch<React.SetStateAction<boolean>>;
    jobListFilter: 'all' | 'completed' | 'failed';
    setJobListFilter: React.Dispatch<React.SetStateAction<'all' | 'completed' | 'failed'>>;
    isAborting: boolean;
    setIsAborting: React.Dispatch<React.SetStateAction<boolean>>;
    closePanel: () => void;
    // Import page state persistence
    importPageState: ImportPageState | null;
    setImportPageState: React.Dispatch<React.SetStateAction<ImportPageState | null>>;
    clearImportPageState: () => void;
}

const BatchProcessingContext = createContext<BatchProcessingContextType | null>(null);

export const useBatchProcessing = () => {
    const context = useContext(BatchProcessingContext);
    if (!context) {
        throw new Error('useBatchProcessing must be used within a BatchProcessingProvider');
    }
    return context;
};

interface BatchProcessingProviderProps {
    children: ReactNode;
}

export const BatchProcessingProvider: React.FC<BatchProcessingProviderProps> = ({ children }) => {
    const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null);
    const [isBatchProcessing, setIsBatchProcessing] = useState(false);
    const [batchComplete, setBatchComplete] = useState(false);
    const [isPanelMinimized, setIsPanelMinimized] = useState(false);
    const [isPanelExpanded, setIsPanelExpanded] = useState(true);
    const [isPanelVisible, setIsPanelVisible] = useState(false);
    const [jobListFilter, setJobListFilter] = useState<'all' | 'completed' | 'failed'>('all');
    const [isAborting, setIsAborting] = useState(false);
    const [importPageState, setImportPageState] = useState<ImportPageState | null>(null);

    const closePanel = () => {
        if (isBatchProcessing) {
            return;
        }
        setBatchStatus(null);
        setBatchComplete(false);
        setIsPanelMinimized(false);
        setIsPanelExpanded(true);
        setIsPanelVisible(false);
    };

    const clearImportPageState = () => {
        setImportPageState(null);
    };

    return (
        <BatchProcessingContext.Provider
            value={{
                batchStatus,
                setBatchStatus,
                isBatchProcessing,
                setIsBatchProcessing,
                batchComplete,
                setBatchComplete,
                isPanelMinimized,
                setIsPanelMinimized,
                isPanelExpanded,
                setIsPanelExpanded,
                isPanelVisible,
                setIsPanelVisible,
                jobListFilter,
                setJobListFilter,
                isAborting,
                setIsAborting,
                closePanel,
                importPageState,
                setImportPageState,
                clearImportPageState
            }}
        >
            {children}
        </BatchProcessingContext.Provider>
    );
};

export default BatchProcessingContext;
